/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from '@azure/msal-browser';

/** Strip accidental quotes from CRA .env values */
const env = (key, fallback = '') =>
    (process.env[key] || fallback).replace(/^["']|["']$/g, '').trim();

const ciamSubdomain = env('REACT_APP_CIAM_SUBDOMAIN', 'CatenaaExternalID');
const tenantId = env('REACT_APP_AAD_TENANT_ID');

/** MSAL authority must NOT include /v2.0 — the library appends it */
const buildAuthority = () => {
    const fromEnv = env('REACT_APP_AAD_AUTHORITY').replace(/\/v2\.0\/?$/, '');
    if (fromEnv) return fromEnv;
    if (tenantId) return `https://${ciamSubdomain}.ciamlogin.com/${tenantId}`;
    return `https://${ciamSubdomain}.ciamlogin.com`;
};

export const ciamKnownAuthority = `${ciamSubdomain.toLowerCase()}.ciamlogin.com`;

export const msalConfig = {
    auth: {
        clientId: env('REACT_APP_CLIENT_ID'),
        authority: buildAuthority(),
        knownAuthorities: [ciamKnownAuthority],
        redirectUri: '/app/home',
        postLogoutRedirectUri: env('REACT_APP_POST_REDIRECT_URL') || '/elystrion-login',
        navigateToLoginRequestUrl: false,
    },
    cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) return;
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        break;
                    case LogLevel.Info:
                        console.info(message);
                        break;
                    case LogLevel.Verbose:
                        console.debug(message);
                        break;
                    case LogLevel.Warning:
                        console.warn(message);
                        break;
                    default:
                        break;
                }
            },
        },
    },
};

/** OIDC scopes only — never add bare scope names or api:// scopes here. */
export const oidcScopes = ['openid', 'profile', 'email', 'offline_access'];

/**
 * Custom API scope for APIM/backend calls.
 *
 * In Entra External ID (CIAM), this MUST point to a SEPARATE single-tenant API app
 * registration — NOT the same SPA client ID. Using api://<spa-client-id>/... causes
 * AADSTS500207 for consumer accounts.
 *
 * Example: api://<api-app-client-id>/access_as_user
 */
const apiScope = env('REACT_APP_API_SCOPE');
const useApiScope = env('REACT_APP_USE_API_SCOPE') === 'true';

export const apiRequest = {
    scopes: useApiScope && apiScope ? [apiScope] : [],
};

/** Interactive sign-in — OIDC only so login never loops. */
export const loginRequest = {
    scopes: oidcScopes,
};

export const getApiTokenScopes = () =>
    apiRequest.scopes.length > 0 ? apiRequest.scopes : oidcScopes;
